import { z } from "zod";

import { ANY_ZONE } from "./types";

const name = z.string().trim().min(1, "Informe um nome.").max(80, "Máximo de 80 caracteres.");
const money = z
  .number({ invalid_type_error: "Informe um valor." })
  .min(0, "O valor não pode ser negativo.")
  .max(1_000_000, "Valor muito alto.");
const pax = z
  .number({ invalid_type_error: "Informe um número." })
  .int("Use números inteiros.")
  .min(1, "Mínimo de 1 passageiro.")
  .max(100, "Máximo de 100 passageiros.");

export const zoneSchema = z.object({
  name,
  order: z.number().int().min(0).default(0),
  active: z.boolean().default(true),
});

export const placeSchema = z.object({
  name,
  zoneId: z.string().min(1, "Selecione uma região."),
  aliases: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  active: z.boolean().default(true),
});

export const tierSchema = z
  .object({ minPax: pax, maxPax: pax, price: money })
  .refine((t) => t.minPax <= t.maxPax, {
    message: "O mínimo não pode ser maior que o máximo.",
    path: ["maxPax"],
  });

export const tiersSchema = z
  .array(tierSchema)
  .min(1, "Adicione pelo menos uma faixa de passageiros.")
  .max(20)
  .superRefine((tiers, ctx) => {
    const sorted = tiers
      .map((t, index) => ({ ...t, index }))
      .sort((a, b) => a.minPax - b.minPax);
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i].minPax <= sorted[i - 1].maxPax) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `A faixa ${sorted[i].minPax}–${sorted[i].maxPax} sobrepõe a faixa ${sorted[i - 1].minPax}–${sorted[i - 1].maxPax}.`,
          path: [sorted[i].index, "minPax"],
        });
      }
    }
  });

const optionalId = z
  .string()
  .optional()
  .nullable()
  .transform((v) => (v ? v : undefined));

export const routeSchema = z
  .object({
    originZoneId: z.string().min(1).default(ANY_ZONE),
    originPlaceId: optionalId,
    destinationZoneId: z.string().min(1, "Selecione o destino."),
    destinationPlaceId: optionalId,
    bidirectional: z.boolean().default(true),
    active: z.boolean().default(true),
    tiers: tiersSchema,
  })
  .refine((r) => !(r.originPlaceId && r.originZoneId === ANY_ZONE), {
    message: "Local de origem precisa de uma região.",
    path: ["originZoneId"],
  })
  .refine(
    (r) => !r.originPlaceId || !r.destinationPlaceId || r.originPlaceId !== r.destinationPlaceId,
    { message: "Origem e destino não podem ser o mesmo local.", path: ["destinationZoneId"] }
  );

export const extraSchema = z.object({
  name,
  price: money,
  type: z.enum(["fixed", "perPax"]).default("fixed"),
  active: z.boolean().default(true),
});

export const configSchema = z.object({
  enabled: z.boolean().default(true),
  whatsappNumber: z
    .string()
    .transform((v) => v.replace(/\D/g, ""))
    .refine((v) => v.length >= 10 && v.length <= 15, {
      message: "Informe o número com DDI e DDD, ex.: 5548999999999.",
    }),
  currency: z.literal("BRL").default("BRL"),
  maxPassengers: z.number().int().min(1).max(50),
  roundTrip: z.object({
    enabled: z.boolean().default(true),
    multiplier: z
      .number({ invalid_type_error: "Informe o multiplicador." })
      .min(1, "O multiplicador mínimo é 1.")
      .max(3, "O multiplicador máximo é 3."),
  }),
  disclaimer: z.string().trim().max(300, "Máximo de 300 caracteres.").default(""),
});

export type ZoneFormValues = z.input<typeof zoneSchema>;
export type PlaceFormValues = z.input<typeof placeSchema>;
export type RouteFormValues = z.input<typeof routeSchema>;
export type ExtraFormValues = z.input<typeof extraSchema>;
export type ConfigFormValues = z.input<typeof configSchema>;

/** Converte os erros do zod em um mapa `caminho → mensagem` para exibir nos formulários. */
export const flattenIssues = (error: z.ZodError): Record<string, string> => {
  const out: Record<string, string> = {};
  error.issues.forEach((issue) => {
    const key = issue.path.join(".") || "_";
    if (!out[key]) out[key] = issue.message;
  });
  return out;
};
